import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore, useToastStore, useModalStore } from '@/store';
import PropTypes from 'prop-types';
import { AGENT_ICON } from '@/constants/config';
import { Image } from 'expo-image';
import AgentDropdown from '@/components/Dropdowns/AgentDropdown';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';

const createStyles = colors => StyleSheet.create({
  row: {
    minHeight: 44,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 4,
  },
  selected: { backgroundColor: colors.solid_color_tertiary },
  main: { flex: 1, minWidth: 0, minHeight: 44, flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  title: { flex: 1, minWidth: 0, color: colors.text_color_primary },
  avatar: { width: 28, height: 28, borderRadius: 14, marginRight: 8, flexShrink: 0 },
  placeholder: { backgroundColor: colors.solid_color_secondary, justifyContent: 'center', alignItems: 'center' },
  menu: { width: 28, height: 36, marginLeft: 2, borderRadius: 6 },
});

function SidebarListItemAgent({
  title,
  isActive,
  href,
  onPress,
  imageUrl = '',
  createdAt,
  shouldShowNewBadge,
  id,
  style,
  ...props
}) {
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const toggleFavoriteAgent = useAuthStore(state => state.toggleFavoriteAgent);
  const favoriteAgentIds = useAuthStore(state => state.user?.favoriteAgentIds) || [];
  const isSaved = favoriteAgentIds.includes(id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownClosedUntil = useRef(0);
  const router = useRouter();
  const { addToast } = useToastStore(state => state.actions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const favoriteAgentsCount = favoriteAgentIds.length;
  const styles = useMemo(() => createStyles(colors), [colors]);

  const handleToggleFavorite = useCallback(async () => {
    try {
      if (!isSaved && isGhost && favoriteAgentsCount >= 1) {
        openModal('GhostSignupModal');
        return;
      }
      if (!isSaved && !isPlus && favoriteAgentsCount >= 5) {
        openModal('PricingModal', {
          title: 'Save More Favorite Bots',
          subtitle: 'Upgrade to Plus to save unlimited favorite bots and access them quickly anytime.',
        });
        return;
      }
      const success = await toggleFavoriteAgent(id);
      if (success) {
        addToast(isSaved ? 'Bot removed from favorites' : 'Bot added to favorites', 'success');
      } else {
        addToast(isSaved ? 'Failed to unsave bot' : 'Failed to save bot', 'error');
      }
    } catch (error) {
      if (error?.name === 'ApiError' && error?.message) {
        addToast(error.message, 'error', error?.data?.actions);
      } else {
        addToast(isSaved ? 'Failed to unsave bot' : 'Failed to save bot', 'error');
      }
    }
  }, [toggleFavoriteAgent, id, addToast, isSaved, favoriteAgentsCount, isGhost, isPlus, openModal]);

  const handleDropdownChange = useCallback(open => {
    setIsDropdownOpen(open);
    // Native menu dismissal can deliver the same touch to the row underneath.
    if (!open) dropdownClosedUntil.current = Date.now() + 200;
  }, []);

  const handlePress = useCallback(() => {
    if (isDropdownOpen || Date.now() < dropdownClosedUntil.current) return;
    if (onPress) onPress();
    else if (href) router.push(href);
  }, [isDropdownOpen, onPress, href, router]);

  return (
    <View
      style={[styles.row, isActive && styles.selected, style]}
      testID={`sidebar-bot-row-${id}`}
      {...(Platform.OS === 'web' ? { dataSet: { sidebarRow: 'true' } } : {})}
    >
      <Pressable
        {...props}
        style={styles.main}
        onPress={handlePress}
        accessibilityLabel={`Bot: ${title}`}
        accessibilityRole="button"
        accessibilityState={{ selected: !!isActive }}
        {...(Platform.OS === 'web' ? { 'aria-current': isActive ? 'page' : undefined } : {})}
      >
        {imageUrl ? (
          <Image source={{ uri: imageUrl }} style={styles.avatar} accessibilityLabel="Bot avatar" contentFit="cover" />
        ) : (
          <View style={[styles.avatar, styles.placeholder]}><Icon name={AGENT_ICON} size={14} color={colors.text_color_secondary} /></View>
        )}
        <Text numberOfLines={1} size="s" weight="semibold" style={styles.title}>{title}</Text>
      </Pressable>
      {/* Native Zeego inspects direct children; AgentDropdown must own Root
          so its Content is visible to the native menu parser. */}
      <View {...(Platform.OS === 'web' ? { dataSet: { sidebarActions: isDropdownOpen ? 'visible' : 'hidden' } } : {})}>
        <AgentDropdown
          agentId={id}
          isSaved={isSaved}
          onToggleSave={handleToggleFavorite}
          open={isDropdownOpen}
          onOpenChange={handleDropdownChange}
          trigger={(
            <NavigationBarButton
              icon="ellipsis"
              title={`More options for ${title}`}
              iconSize={14}
              style={styles.menu}
              color={colors.text_color_secondary}
              onPress={event => {
                // The Mac trigger treats preventDefault as a request to keep the menu closed.
                if (Platform.OS !== 'macos') event?.preventDefault?.();
                event?.stopPropagation?.();
              }}
            />
          )}
        />
      </View>
    </View>
  );
}

SidebarListItemAgent.propTypes = {
  title: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  href: PropTypes.string,
  onPress: PropTypes.func,
  imageUrl: PropTypes.string,
  id: PropTypes.string.isRequired,
};

export default React.memo(SidebarListItemAgent);
